import {
  CertificateCreationRequest,
  CertificateList,
  CertificateRepresentation,
  CertificateRevocationRequest,
  CertificateUploadRequest,
  CreatedCertificate,
  OrganizationRepresentation,
  RevocationReason,
} from "../infrastructure/api/api";
import { getHalJSON, post, remove } from "../infrastructure/api/digipost-fetch";
import { cached, invalidate } from "../infrastructure/api/cache";
import {
  findUriIfExists,
  MissingLinkError,
} from "../infrastructure/entrypoint/link";

export interface CertificatesApi {
  listCertificates: () => Promise<CertificateRepresentation[]>;
  findCertificate: (
    certificateId: string,
  ) => Promise<CertificateRepresentation | undefined>;
  createCertificate: (
    request: CertificateCreationRequest,
  ) => Promise<CreatedCertificate>;
  uploadCertificate: (
    request: CertificateUploadRequest,
  ) => Promise<CertificateRepresentation>;
  revokeCertificate: (
    certificateId: string,
    reason: RevocationReason,
  ) => Promise<void>;
  unregisterCertificate: (certificateId: string) => Promise<void>;
}

export function certificatesApi(
  organization: OrganizationRepresentation,
): CertificatesApi {
  const certificatesUrl = findUriIfExists("certificates", organization._links);

  const listCertificates = (): Promise<CertificateRepresentation[]> =>
    certificatesUrl
      ? cached(
          certificatesUrl,
          async () =>
            (await getHalJSON<CertificateList>(certificatesUrl)).certificates,
        )
      : Promise.resolve([]);

  const invalidateCertificates = () => {
    if (certificatesUrl) {
      invalidate(certificatesUrl);
    }
  };

  const findCertificate = async (certificateId: string) =>
    (await listCertificates()).find(
      (cert) => cert.certificate.id === certificateId,
    );

  return {
    listCertificates,
    findCertificate,

    createCertificate: async (request) => {
      const createUrl = findUriIfExists(
        "create_certificate",
        organization._links,
      );
      if (!createUrl) {
        throw new MissingLinkError("create_certificate");
      }

      const response = await post(createUrl, request);
      invalidateCertificates();

      return response.json();
    },

    uploadCertificate: async (request) => {
      const uploadUrl = findUriIfExists(
        "upload_certificate",
        organization._links,
      );
      if (!uploadUrl) {
        throw new MissingLinkError("upload_certificate");
      }

      const response = await post(uploadUrl, request);
      invalidateCertificates();

      return response.json();
    },

    revokeCertificate: async (certificateId, reason) => {
      const certificate = await findCertificate(certificateId);
      const revokeUrl =
        certificate && findUriIfExists("revoke", certificate._links);
      if (!revokeUrl) {
        throw new MissingLinkError("revoke");
      }

      const body: CertificateRevocationRequest = { reason };
      await post(revokeUrl, body);
      invalidateCertificates();
    },

    unregisterCertificate: async (certificateId) => {
      const certificate = await findCertificate(certificateId);
      const unregisterUrl =
        certificate && findUriIfExists("unregister", certificate._links);
      if (!unregisterUrl) {
        throw new MissingLinkError("unregister");
      }

      await remove(unregisterUrl, "");
      invalidateCertificates();
    },
  };
}
